import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  DRAWER_KEY,
  FIREBASE_EVENTS,
  MODAL_DRAWER_KEY,
  STORE_STAGES,
  STORE_STAGES_ALLOW_CART_ICON,
  STORES_STAGES_TITLE,
  STORES_STAGES_TITLE_DEFAULT,
} from 'app-constants'
import { useFirebaseEvent } from 'hooks'
import {
  goBack,
  setAllowCloseOnReturnButton,
  setDrawer,
  setDrawerModal,
  setHeaderVisibility,
  setStage,
} from 'slices/drawer'
import { setDetailProduct } from 'slices/store'
import { fetchBanners } from 'slices/banners'
import { productService } from 'services'
import { ContextLoader } from 'components/context-loader'
import { CartIcon } from 'components/cart-icon'
import { DrawerHeader } from 'components/drawer-header'
import { ACTIVATION_PRODUCT_INITIAL_STATE } from './constants'
import {
  AccessStoreError,
  ProductDetail,
  Cart,
  StoreProducts,
  Success,
  ProductActivation,
  IneligibleStoreError,
} from './components'

function stages(props) {
  return {
    [STORE_STAGES.INITIAL]: <StoreProducts {...props} />,
    [STORE_STAGES.INELIGIBLE]: <IneligibleStoreError {...props} />,
    [STORE_STAGES.ACCESS_ERROR]: <AccessStoreError {...props} />,
    [STORE_STAGES.CART]: <Cart {...props} />,
    [STORE_STAGES.DETAIL]: <ProductDetail {...props} />,
    [STORE_STAGES.ACTIVATION]: <ProductActivation {...props} />,
    [STORE_STAGES.SUCCESS]: <Success {...props} />,
  }
}

export function StoreDrawer() {
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [cart, setCart] = useState([])
  const [hasAccessError, setHasAccessError] = useState(false)
  const [categories, setCategories] = useState([])
  const [activationInfo, setActivationInfo] = useState(ACTIVATION_PRODUCT_INITIAL_STATE)
  const [allProducts, setAllProducts] = useState([])
  const [hideBackButton, setHideBackButton] = useState(false)
  const { stage } = useSelector(({ drawer }) => drawer)
  const { detailProduct, isEligibleToBuy, isLoadingElegible } = useSelector(state => state.store)
  const { id: contractId } = useSelector(state => state.contract.selected)
  const [totalValue, setTotalValue] = useState(0)
  const dispatch = useDispatch()
  const { sendEvent } = useFirebaseEvent()
  const { getProductRecommendations } = productService()

  const numberOfItems = cart.length

  useEffect(() => {
    if (!isEligibleToBuy && !isLoadingElegible) {
      dispatch(setStage(STORE_STAGES.INELIGIBLE))
      setLoading(false)
    }
  }, [isEligibleToBuy, dispatch, isLoadingElegible])

  useEffect(() => {
    function resolveAllProductsList(allCategories) {
      const products = allCategories.flatMap(category => category.products || [])
      setAllProducts(products)
    }

    async function fetchProducts() {
      try {
        const { data } = await getProductRecommendations(contractId)
        if (data.length > 0) {
          setCategories(data)
          resolveAllProductsList(data)
        } else {
          setHasAccessError(true)
        }
      } catch (e) {
        console.error(e)
        setHasAccessError(true)
      } finally {
        setLoading(false)
      }
    }

    dispatch(setHeaderVisibility(false))
    dispatch(setAllowCloseOnReturnButton(true))
    if (isEligibleToBuy) {
      fetchProducts()
    }
  }, [isEligibleToBuy, contractId, dispatch])

  useEffect(() => {
    function handleProductFromBannerRedirect() {
      if (detailProduct) {
        setSelectedProduct(detailProduct)
        dispatch(setStage(STORE_STAGES.DETAIL))
      }
    }

    handleProductFromBannerRedirect()
  }, [detailProduct, dispatch])

  useEffect(() => {
    const value = cart.reduce((total, item) => total + (item.value || 0), 0)
    setTotalValue(value)
  }, [cart])

  function handleOpenProductDetail(product) {
    setSelectedProduct(product)
    if (product) {
      dispatch(setStage(STORE_STAGES.DETAIL))
    }
  }

  function handleAddToCart(selectedOption, familyName) {
    const hasOptionInCart = cart.some(option => option.id === selectedOption.id)
    if (!hasOptionInCart) {
      const newItem = { ...selectedOption, familyName }
      const indexCurrentFamilyNameOption = cart.findIndex(item => item.familyName === familyName)

      if (indexCurrentFamilyNameOption < 0) {
        setCart(items => [...items, newItem])
      } else {
        const updatedCart = [...cart]
        updatedCart.splice(indexCurrentFamilyNameOption, 1, newItem)
        setCart(updatedCart)
      }
    }
  }

  function handleRemoveFromCart(id) {
    setCart(items => items.filter(item => item.id !== id))
  }

  function handleDrawerClose() {
    setActivationInfo(ACTIVATION_PRODUCT_INITIAL_STATE)
    dispatch(fetchBanners(contractId))
    dispatch(setDrawer(DRAWER_KEY.NONE))
    dispatch(setDetailProduct(null))
  }

  function handleBeforeClose() {
    const hasEmptyCartAndDetailStage = !numberOfItems && STORE_STAGES.DETAIL === stage

    if (hasEmptyCartAndDetailStage) {
      dispatch(setDrawerModal({ key: MODAL_DRAWER_KEY.STORE_WITHDRAWAL }))
    } else {
      handleDrawerClose()
    }
  }

  function handleRedirectToCart() {
    if (showCartIconButton()) {
      sendEvent(FIREBASE_EVENTS.SOFTBUNDLE_SEE_CART)
      dispatch(setStage(STORE_STAGES.CART))
    } else {
      handleBeforeClose()
    }
  }

  function handleBeforeGoBack() {
    const isInitialStage = STORE_STAGES.INITIAL === stage

    if (!isEligibleToBuy || isInitialStage) {
      handleDrawerClose()
      return
    }

    dispatch(goBack())
  }

  function showCartIconButton() {
    return numberOfItems && STORE_STAGES_ALLOW_CART_ICON.includes(stage)
  }

  function renderCartIcon() {
    if (showCartIconButton()) {
      return <CartIcon numberOfItems={numberOfItems} />
    }
  }

  function renderContent() {
    if (loading) return <ContextLoader fixed={false} />

    if (hasAccessError) return <AccessStoreError />

    return stages({
      categories,
      selectedProduct,
      handleOpenProductDetail,
      handleAddToCart,
      activationInfo,
      setActivationInfo,
      handleRemoveFromCart,
      cart,
      allProducts,
      totalValue,
      setHideBackButton,
    })[stage]
  }

  return (
    <>
      <DrawerHeader
        onClose={handleBeforeClose}
        title={STORES_STAGES_TITLE[stage] || STORES_STAGES_TITLE_DEFAULT}
        rightButtonIcon={renderCartIcon()}
        onRightButtonClick={handleRedirectToCart}
        onLeftButtonClick={handleBeforeGoBack}
        hideBackButton={hideBackButton}
      />
      {renderContent()}
    </>
  )
}
